import { useState, useRef } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { FileText, Sparkles, Upload, ChevronRight, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  Applied: "bg-[#E5E1DB] text-[#5C6B64]",
  Interviewing: "bg-[#FDF2DF] text-[#8B6A1F]",
  Offer: "bg-[#E2F0E7] text-[#1A362D]",
  Rejected: "bg-[#FCECEC] text-[#C65D47]",
};

export default function ResumeImportModal({ open, onOpenChange, onImported, welcome = false }) {
  const fileRef = useRef(null);
  const [stage, setStage] = useState("upload"); // upload | preview
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [fileName, setFileName] = useState("");

  const reset = () => {
    setStage("upload");
    setRows([]);
    setSelected(new Set());
    setFileName("");
    setParsing(false);
    setImporting(false);
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("File is too large (max 2 MB).");
      return;
    }
    setFileName(f.name);
    setParsing(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/ai/extract-resume", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const found = Array.isArray(data?.applications) ? data.applications : [];
      if (found.length === 0) {
        toast.error("Couldn't find any roles in that resume. Try a different file.");
        setParsing(false);
        return;
      }
      setRows(found);
      // Pre-select all by default
      setSelected(new Set(found.map((_, i) => i)));
      setStage("preview");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggle = (idx) => {
    const next = new Set(selected);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((_, i) => i)));
  };

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const handleImport = async () => {
    const picks = rows.filter((_, i) => selected.has(i));
    if (picks.length === 0) {
      toast.error("Pick at least one role to add.");
      return;
    }
    setImporting(true);
    try {
      const { data } = await api.post("/ai/resume-import", { applications: picks });
      const parts = [`${data.created} added`];
      if (data.duplicates) parts.push(`${data.duplicates} already on your pipeline`);
      if (data.limit_reached && data.skipped > (data.duplicates || 0)) {
        const freeSkipped = data.skipped - (data.duplicates || 0);
        parts.push(`${freeSkipped} skipped (free plan limit)`);
      }
      const msg = parts.join(" · ");
      if (data.limit_reached) {
        toast.success(`${msg}. Upgrade to add the rest.`);
      } else {
        toast.success(`${msg}.`);
      }
      onImported?.({
        created: data.created,
        duplicates: data.duplicates || 0,
        skipped: data.skipped || 0,
        limit_reached: !!data.limit_reached,
      });
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="max-w-3xl bg-[#FDFCFA] max-h-[90vh] overflow-y-auto" data-testid="resume-import-modal">
        <DialogHeader>
          {welcome && stage === "upload" && (
            <p className="text-xs tracking-[0.2em] uppercase text-[#D99C3D]" data-testid="welcome-eyebrow">
              Welcome to Trackr
            </p>
          )}
          <DialogTitle className="font-display text-2xl text-[#1A362D] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#D99C3D]" />
            {welcome && stage === "upload" ? "Let's prefill your pipeline" : "Jumpstart from your resume"}
          </DialogTitle>
          <DialogDescription>
            {welcome && stage === "upload"
              ? "Upload your resume and Trackr will seed your pipeline with your past roles — then you can start tracking new applications on top of a filled-in canvas."
              : "Upload your resume (PDF, DOCX, or TXT) — Trackr will extract past roles so you can import them in one click."}
          </DialogDescription>
        </DialogHeader>

        {stage === "upload" && (
          <div className="py-6">
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.docx,.txt,.md"
              onChange={handleFile}
              className="hidden"
              data-testid="resume-file-input"
            />
            <div
              onClick={() => !parsing && fileRef.current?.click()}
              className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${
                parsing
                  ? "border-[#D99C3D] bg-[#FDF2DF]/30 cursor-wait"
                  : "border-[#D4CFC5] bg-white hover:border-[#1A362D] hover:bg-[#F4F1EE]"
              }`}
              data-testid="resume-dropzone"
            >
              {parsing ? (
                <>
                  <Loader2 className="w-10 h-10 mx-auto text-[#D99C3D] animate-spin" />
                  <p className="font-display text-xl mt-4 text-[#1A362D]">Reading {fileName}…</p>
                  <p className="text-sm text-[#5C6B64] mt-1">Extracting past roles with AI.</p>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 mx-auto rounded-full bg-[#F4F1EE] flex items-center justify-center">
                    <Upload className="w-6 h-6 text-[#1A362D]" />
                  </div>
                  <p className="font-display text-xl mt-4 text-[#1A362D]">Drop or click to upload</p>
                  <p className="text-sm text-[#5C6B64] mt-1">PDF, DOCX, or TXT · up to 2 MB</p>
                </>
              )}
            </div>
            <p className="text-xs text-[#5C6B64] mt-4 flex items-start gap-2">
              <FileText className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Nothing is stored — your resume content is parsed in memory and discarded after extraction.
            </p>
          </div>
        )}

        {stage === "preview" && (
          <div className="py-2">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-[#5C6B64]">
                Found <span className="font-semibold text-[#1A362D]">{rows.length}</span> role{rows.length === 1 ? "" : "s"}. Uncheck anything you don't want to import.
              </p>
              <button
                onClick={toggleAll}
                className="text-xs underline text-[#1A362D] hover:text-[#C65D47]"
                data-testid="toggle-all-btn"
              >
                {selected.size === rows.length ? "Unselect all" : "Select all"}
              </button>
            </div>
            <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
              {rows.map((r, i) => (
                <div
                  key={i}
                  className={`rounded-xl border p-4 bg-white transition-all ${
                    selected.has(i) ? "border-[#1A362D] shadow-sm" : "border-[#F0EDE7] opacity-60"
                  }`}
                  data-testid={`resume-row-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selected.has(i)}
                      onCheckedChange={() => toggle(i)}
                      className="mt-1"
                      data-testid={`resume-checkbox-${i}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <input
                          value={r.company}
                          onChange={(e) => updateRow(i, "company", e.target.value)}
                          className="font-semibold text-[#1A362D] bg-transparent border-b border-transparent hover:border-[#D4CFC5] focus:border-[#1A362D] focus:outline-none"
                          data-testid={`resume-company-${i}`}
                        />
                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status] || STATUS_STYLE.Offer}`}>
                          {r.status}
                        </span>
                      </div>
                      <input
                        value={r.job_title}
                        onChange={(e) => updateRow(i, "job_title", e.target.value)}
                        className="text-sm text-[#5C6B64] mt-0.5 w-full bg-transparent border-b border-transparent hover:border-[#D4CFC5] focus:border-[#1A362D] focus:outline-none"
                        data-testid={`resume-title-${i}`}
                      />
                      <div className="flex items-center gap-3 mt-2 text-xs text-[#8B8378]">
                        {r.date_applied && <span>Started {r.date_applied}</span>}
                        {r.notes && <span className="truncate flex-1" title={r.notes}>· {r.notes}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {stage === "upload" ? (
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="rounded-full"
              disabled={parsing}
              data-testid="resume-cancel-btn"
            >
              {welcome ? "Skip for now" : "Cancel"}
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => setStage("upload")}
                className="rounded-full"
                disabled={importing}
                data-testid="resume-back-btn"
              >
                Pick a different file
              </Button>
              <Button
                onClick={handleImport}
                disabled={importing || selected.size === 0}
                className="rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white px-6"
                data-testid="resume-import-submit"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Adding…
                  </>
                ) : (
                  <>
                    Add {selected.size} role{selected.size === 1 ? "" : "s"} <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
